/*
 * SEO KEYWORD MAP — CONTADOR DE HORAS
 * URL: /contador-de-horas
 * Primary: contador de horas (27K vol, KD 30)
 * Secondary: contar horas (8.1K, KD 26), contagem de horas (5.4K, KD 26)
 * LSI: cronômetro online, timer, temporizador, contar tempo,
 *       marcador de horas, registrar horas trabalhadas
 * Total addressable: ~40,500/mo
 * Page type: Timer/Counter Tool + Guide
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { Play, Pause, RotateCcw, Flag } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const ContadorHorasPage = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // in ms
  const [laps, setLaps] = useState([]);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const start = useCallback(() => {
    startTimeRef.current = Date.now() - elapsed;
    intervalRef.current = setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 100);
    setIsRunning(true);
  }, [elapsed]);

  const pause = () => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
  };

  const reset = () => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setElapsed(0);
    setLaps([]);
  };

  const addLap = () => {
    setLaps(prev => [...prev, elapsed]);
  };

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatDecimal = (ms) => {
    return formatNumber(ms / 3600000, 4);
  };

  const faqs = [
    {
      question: 'O que é um contador de horas?',
      answer: 'Um contador de horas é uma ferramenta que registra o tempo decorrido em horas, minutos e segundos. Funciona como um cronômetro, mas com foco na contagem de períodos mais longos — ideal para controlar horas de trabalho, estudo ou projetos.'
    },
    {
      question: 'Como contar horas trabalhadas?',
      answer: 'Para contar horas trabalhadas, inicie o contador quando começar a trabalhar e pause quando terminar. Cada vez que fizer uma pausa, pare o contador e retome ao voltar. No final do dia, o total acumulado mostra suas horas trabalhadas com precisão.'
    },
    {
      question: 'Como fazer a contagem de horas no mês?',
      answer: 'Para fazer a contagem de horas mensal, anote o total diário e some todos os dias no final do mês. Ou use o botão "Marcar volta" para registrar cada dia. Para uma jornada CLT de 44 horas semanais, você trabalha cerca de 191 horas por mês (44 × 4,35 semanas). O número 220 que aparece na folha é o divisor salarial, que inclui o descanso semanal remunerado.'
    },
    {
      question: 'Qual a diferença entre contador e cronômetro?',
      answer: 'Na prática, são similares. O cronômetro é mais voltado para medir tempos curtos (corridas, cozimento). O contador de horas é otimizado para períodos mais longos, exibindo o tempo em formato HH:MM:SS e horas decimais, mais úteis para controle de jornada.'
    },
    {
      question: 'O contador funciona com a tela do celular bloqueada?',
      answer: 'Sim. Nosso contador usa a hora do sistema para calcular o tempo decorrido, então mesmo que você troque de aba ou bloqueie a tela, ao voltar o tempo estará correto. Porém, se você fechar completamente o navegador, o progresso será perdido.'
    },
    {
      question: 'Quantas horas devo trabalhar por dia segundo a CLT?',
      answer: 'A CLT estabelece jornada máxima de 8 horas diárias e 44 horas semanais (Art. 58). Existem jornadas reduzidas, como a de 6 horas para turnos ininterruptos de revezamento (Art. 7º, XIV da Constituição) e o regime de tempo parcial de até 30 horas semanais (Art. 58-A da CLT). Use o contador para verificar se sua jornada diária está dentro do limite.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Contador de Horas Online",
    "url": "https://calculadoradehoras.net.br/contador-de-horas",
    "description": "Contador de horas online gratuito. Cronômetro para contar horas trabalhadas, de estudo e projetos. Com marcação de voltas e resultado em decimal.",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "All",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "BRL" },
    "inLanguage": "pt-BR"
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Contador de Horas Online — Cronômetro de Trabalho</title>
        <meta name="description" content="Contador de horas online grátis. Conte horas trabalhadas, de estudo ou projetos com cronômetro preciso. Resultado em HH:MM:SS e decimal. Sem cadastro." />
        <link rel="canonical" href="https://calculadoradehoras.net.br/contador-de-horas" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Contador de Horas Online — Cronômetro de Trabalho" />
        <meta property="og:description" content="Conte horas trabalhadas, de estudo ou projetos com cronômetro preciso. Resultado em HH:MM:SS e decimal." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/contador-de-horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contador de Horas Online — Cronômetro de Trabalho" />
        <meta name="twitter:description" content="Conte horas trabalhadas, de estudo ou projetos com cronômetro preciso. Resultado em HH:MM:SS e decimal." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={webAppSchema} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Contador de Horas","item":"https://calculadoradehoras.net.br/contador-de-horas"}
        ]
      }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Contador de Horas</span>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-dark-text mb-3">
            Contador de Horas Online
          </h1>
          <p className="text-lg text-body-text max-w-2xl mx-auto">
            Cronômetro gratuito para contar horas de trabalho, estudo ou qualquer atividade. Resultado em tempo real com formato HH:MM:SS e horas decimais.
          </p>
        </div>

        {/* Timer Display */}
        <div className="bg-white rounded-xl border-2 border-border-gray p-6 md:p-8 mb-8 text-center">
          <h2 className="text-xl font-heading font-bold text-dark-text mb-6">Iniciar Contagem de Horas</h2>

          <div className="text-6xl md:text-7xl font-mono font-bold text-dark-text mb-2 tabular-nums">
            {formatTime(elapsed)}
          </div>
          <p className="text-lg text-body-text mb-8">{`${formatDecimal(elapsed)} horas decimais`}</p>

          <div className="flex justify-center gap-3 flex-wrap">
            {!isRunning ? (
              <button onClick={start} className="flex items-center gap-2 px-8 py-3 bg-success-green text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity">
                <Play size={20} /> Iniciar
              </button>
            ) : (
              <button onClick={pause} className="flex items-center gap-2 px-8 py-3 bg-orange-accent text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity">
                <Pause size={20} /> Pausar
              </button>
            )}
            <button onClick={addLap} disabled={!isRunning} className="flex items-center gap-2 px-6 py-3 bg-blue-primary text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-40">
              <Flag size={18} /> Marcar Volta
            </button>
            <button onClick={reset} className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-body-text rounded-lg font-bold hover:bg-gray-200 transition-colors">
              <RotateCcw size={18} /> Resetar
            </button>
          </div>

          {laps.length > 0 && (
            <div className="mt-8 text-left">
              <h3 className="font-semibold text-dark-text mb-3">Voltas Registradas</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {laps.map((lap, i) => (
                  <div key={i} className="flex justify-between items-center bg-light-bg rounded-lg px-4 py-2">
                    <span className="text-sm font-semibold text-body-text">Volta {i + 1}</span>
                    <span className="font-mono text-dark-text">{formatTime(lap)}</span>
                    <span className="text-sm text-body-text">{formatDecimal(lap)}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        {/* Content */}
        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">O Que é um Contador de Horas?</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O <strong>contador de horas</strong> é uma ferramenta que funciona como um cronômetro digital, mas otimizada para medir períodos mais longos. Enquanto um cronômetro convencional mede segundos e centésimos, nosso contador de horas exibe o tempo em formato <strong>HH:MM:SS</strong> e <strong>horas decimais</strong> — os dois formatos mais usados no controle de jornada de trabalho.
            </p>
            <p className="text-body-text leading-relaxed mb-4">
              A <strong>contagem de horas</strong> é essencial para profissionais autônomos, freelancers, estudantes e qualquer pessoa que precise registrar o tempo gasto em atividades. Com a função de marcação de voltas, você pode registrar o tempo parcial de cada tarefa sem parar a contagem total.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Como Contar Horas de Trabalho</h2>
            <p className="text-body-text leading-relaxed mb-4">
              Para contar suas horas trabalhadas usando esta ferramenta, siga estes passos simples.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-body-text mb-4">
              <li>Clique em <strong>"Iniciar"</strong> quando começar a trabalhar.</li>
              <li>Clique em <strong>"Pausar"</strong> quando fizer uma pausa (almoço, café, etc.).</li>
              <li>Clique em <strong>"Iniciar"</strong> novamente quando retomar o trabalho — o tempo continua de onde parou.</li>
              <li>Use <strong>"Marcar Volta"</strong> para registrar o tempo parcial de cada tarefa ou período.</li>
              <li>No final do dia, o contador mostra o total real de horas trabalhadas.</li>
            </ol>
            <p className="text-body-text leading-relaxed">
              O formato decimal exibido é o padrão usado em folhas de pagamento. Por exemplo, se o contador marca 08:30:00, o valor decimal será 8,5000 horas — exatamente como aparecerá no seu holerite.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Jornada de Trabalho na CLT</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O <strong>Art. 58 da CLT</strong> estabelece que a jornada padrão é de 8 horas diárias e 44 horas semanais. Usar um contador de horas ajuda a verificar se você está dentro dos limites legais e se tem horas extras a receber.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-light-bg rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-dark-text">8h</p>
                <p className="text-sm text-body-text">Jornada diária máxima</p>
              </div>
              <div className="bg-light-bg rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-dark-text">44h</p>
                <p className="text-sm text-body-text">Jornada semanal (CLT)</p>
              </div>
              <div className="bg-light-bg rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-dark-text">~191h</p>
                <p className="text-sm text-body-text">Horas trabalhadas/mês (44h/sem)</p>
              </div>
            </div>
            <p className="text-body-text leading-relaxed">
              Para calcular o valor das horas que excederem esses limites, use nossa <a href="/calculadora-de-horas-extras" className="text-blue-primary hover:underline">calculadora de horas extras</a>. Para somar as horas da semana inteira, experimente a <a href="/" className="text-blue-primary hover:underline">calculadora de horas</a>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Dicas para Contar Horas com Eficiência</h2>
            <ul className="list-disc list-inside space-y-3 text-body-text">
              <li><strong>Marque voltas por tarefa:</strong> Use a função de voltas para separar o tempo de cada projeto ou atividade. Assim você sabe exatamente quanto tempo dedicou a cada tarefa.</li>
              <li><strong>Desconte intervalos:</strong> Sempre pause o contador durante intervalos como almoço ou café. A CLT exige intervalo mínimo de 1 hora para jornadas acima de 6 horas (Art. 71).</li>
              <li><strong>Anote o total diário:</strong> No final de cada dia, registre o tempo em uma planilha ou caderno. Isso facilita a soma semanal e mensal.</li>
              <li><strong>Compare com o holerite:</strong> Use o valor em horas decimais para comparar com o que aparece na sua folha de pagamento e identificar possíveis diferenças.</li>
            </ul>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/contador-de-horas" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default ContadorHorasPage;
